import { t } from "@lingui/macro";
import { CALL_STATUS } from "@repo/common";
import {
	type OutboundCallContext,
	resolveCallParticipantDisplay,
} from "@repo/ui";
import {
	AlertCircle,
	Archive,
	ArrowLeft,
	ArrowRight,
	FileText,
	FlaskConical,
	Loader2,
	Mail,
	MailOpen,
	MicOff,
	PhoneForwarded,
	Play,
} from "lucide-react";
import { useData } from "../contexts/DataContext";
import { useServiceTags } from "../hooks/useServiceTags";
import { i18n } from "../i18n";
import type { Call } from "../types";
import { isCallUnread } from "../utils/callReadStatus";
import { resolveCallServiceId } from "../utils/callServices";
import { formatCaseId } from "../utils/formatCaseId";
import { formatPhoneNumber } from "../utils/formatPhoneNumber";
import { getServiceTagClassName, resolveTagIds } from "../utils/serviceTags";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

export interface CallRecordProps {
	call: Call;
	onClick: () => void;
	onPlay?: (e: React.MouseEvent) => void;
	onArchive?: (e: React.MouseEvent) => void;
	onMarkRead?: (e: React.MouseEvent) => void;
	onMarkUnread?: (e: React.MouseEvent) => void;
	language: "en" | "he";
	isAdmin: boolean;
	compact?: boolean;
	getAgentCompany?: (agentName: string) => string | undefined;
	outboundContext?: OutboundCallContext;
}

export function CallRecord({
	call,
	onClick,
	onPlay,
	onArchive,
	onMarkRead,
	onMarkUnread,
	language,
	isAdmin: _isAdmin,
	compact = false,
	getAgentCompany: _getAgentCompany,
	outboundContext,
}: CallRecordProps) {
	const { user, agents, services } = useData();
	const resolvedServiceId = resolveCallServiceId(call, agents, services);
	const liveServiceTags = useServiceTags(user?.companyId, resolvedServiceId);
	if (call.piiRemovalPending) {
		return (
			<div
				data-testid={`call-row-${call.id}`}
				data-call-id={call.id}
				className="rounded-lg border-[1.5px] border-card-border bg-card p-3 shadow-card"
			>
				<div className="flex flex-wrap items-center justify-between gap-2">
					<Badge variant="secondary">
						{call.manualResolutionRequired
							? t({
									id: "piiManualResolution.title",
									message: "Privacy review required",
								})
							: t({
									id: "piiRemovalPending.title",
									message: "PII removal pending",
								})}
					</Badge>
					<span className="text-xs text-muted-foreground">
						{call.caseId != null && (
							<>
								{t({ id: "caseId_label", message: "Case ID" })}:{" "}
								{formatCaseId(call.caseId)} ·{" "}
							</>
						)}
						{call.date} · {call.time}
					</span>
				</div>
				<p className="mt-2 text-sm text-muted-foreground">
					{call.manualResolutionRequired
						? t({
								id: "piiManualResolution.description",
								message:
									"Call details will be available after the privacy review is resolved.",
							})
						: t({
								id: "piiRemovalPending.description",
								message:
									"Call details will be available after PII removal is complete.",
							})}
				</p>
			</div>
		);
	}
	const participants = resolveCallParticipantDisplay(call, outboundContext);
	const isNew = isCallUnread(call.interactionStatus);

	// Get action items (max 2), prioritizing open/in-progress
	const actionItems = call.actionItems
		? [...call.actionItems]
				.sort((a, b) => {
					const rank = (status?: string) => {
						if (status === "open") return 0;
						if (status === "in-progress") return 1;
						return 2;
					};
					return rank(a.status) - rank(b.status);
				})
				.slice(0, 2)
		: [];
	const callTags = resolveTagIds(call.tagIds, liveServiceTags).slice(0, 3);
	const hasNoTranscript = !call.transcript?.trim();
	const isProcessingCall =
		hasNoTranscript &&
		(call.status === CALL_STATUS.PROCESSING ||
			call.status === CALL_STATUS.IN_PROGRESS);
	const isFailedWithoutTranscript =
		hasNoTranscript && call.status === CALL_STATUS.FAILED;
	const shouldShowNoAudio =
		hasNoTranscript &&
		(call.status === CALL_STATUS.COMPLETED ||
			call.status === CALL_STATUS.ABANDONED);
	const shouldShowStatusFallback =
		isProcessingCall || isFailedWithoutTranscript || shouldShowNoAudio;
	const fallbackTitle = isProcessingCall
		? t({ id: "call_processing_title", message: "Processing call" })
		: isFailedWithoutTranscript
			? t({ id: "call_processing_failed_title", message: "Processing failed" })
			: t({ id: "no_audio_title", message: "No Audio / Silence" });
	const fallbackSummary = isProcessingCall
		? t({
				id: "call_processing_summary",
				message:
					"Call processing is still in progress. Transcript and summary will appear when processing finishes.",
			})
		: isFailedWithoutTranscript
			? t({
					id: "call_processing_failed_summary",
					message: "Call processing failed before a transcript was created.",
				})
			: t({
					id: "no_audio_summary",
					message: "The call contained no recognizable audio or speech.",
				});

	const mobileParticipant = participants.isOutbound
		? participants.callee
		: participants.caller;

	const testNumberBadge = call.isTestCall && (
		<Badge
			variant="outline"
			className="h-5 min-w-0 max-w-full border-ring/30 bg-accent px-2 text-[11px] font-semibold text-accent-foreground"
		>
			<FlaskConical className="w-3 h-3" />
			<span className="truncate">
				{t({ id: "testNumberBadge", message: "Test Number" })}
			</span>
		</Badge>
	);
	const humanForwardedBadge = participants.isHuman &&
		!participants.isOutbound && (
			<Badge
				variant="outline"
				className="h-5 min-w-0 max-w-full border-warn/30 bg-warn-tint px-2 text-[11px] font-semibold text-foreground"
			>
				<PhoneForwarded className="w-3 h-3" />
				<span className="truncate">
					{t({ id: "humanForwardedBadge", message: "Forwarded to Operator" })}
				</span>
			</Badge>
		);
	const playbookName = outboundContext?.playbookName;
	const isPlaybookCall = Boolean(
		playbookName ||
			call.outboundPlaybookId ||
			call.source === "outbound_playbook_task",
	);
	// The task's latest outcome may belong to a later call; use this attempt only.
	const attemptOutcome = outboundContext?.attempt?.outcome;
	const playbookLabel =
		playbookName || t({ id: "callPlaybookBadge", message: "Playbook" });
	const playbookBadgeLabel = attemptOutcome
		? `${playbookLabel} — ${attemptOutcome}`
		: playbookLabel;
	const playbookBadge = isPlaybookCall && (
		<Badge
			title={playbookBadgeLabel}
			className="h-5 min-w-0 max-w-full border border-live/30 bg-live-tint px-2 text-[11px] font-semibold text-foreground"
		>
			<span className="truncate">{playbookBadgeLabel}</span>
		</Badge>
	);

	const actions = !call.isBlocked && (
		<>
			<Button
				variant="outline"
				size="sm"
				className="h-10 w-10 shrink-0 p-0 lg:h-7 lg:w-7"
				disabled={!onPlay}
				onClick={(e) => {
					e.stopPropagation();
					onPlay?.(e);
				}}
				title={t({ id: "playAudio", message: "Play audio" })}
				aria-label={t({ id: "playAudio", message: "Play audio" })}
			>
				<Play className="h-3.5 w-3.5" />
			</Button>
			<Button
				variant="outline"
				size="sm"
				className="h-10 w-10 shrink-0 p-0 lg:h-7 lg:w-7"
				disabled={isNew ? !onMarkRead : !onMarkUnread}
				onClick={(e) => {
					e.stopPropagation();
					(isNew ? onMarkRead : onMarkUnread)?.(e);
				}}
				title={
					isNew
						? t({ id: "markAsRead", message: "Mark as read" })
						: t({ id: "markAsUnread", message: "Mark as unread" })
				}
				aria-label={
					isNew
						? t({ id: "markAsRead", message: "Mark as read" })
						: t({ id: "markAsUnread", message: "Mark as unread" })
				}
			>
				{isNew ? (
					<MailOpen className="h-3.5 w-3.5" />
				) : (
					<Mail className="h-3.5 w-3.5" />
				)}
			</Button>
			<Button
				variant="outline"
				size="sm"
				className="h-10 w-10 shrink-0 p-0 lg:h-7 lg:w-7"
				disabled={!onArchive}
				onClick={(e) => {
					e.stopPropagation();
					onArchive?.(e);
				}}
				title={
					call.interactionStatus === "archived"
						? t({ id: "unarchive", message: "Unarchive" })
						: t({ id: "archive", message: "Archive" })
				}
				aria-label={
					call.interactionStatus === "archived"
						? t({ id: "unarchive", message: "Unarchive" })
						: t({ id: "archive", message: "Archive" })
				}
			>
				<Archive
					className={`h-3.5 w-3.5 ${call.interactionStatus === "archived" ? "text-accent-ink fill-current" : ""}`}
				/>
			</Button>
		</>
	);
	const specialBadges = (playbookBadge ||
		humanForwardedBadge ||
		testNumberBadge) && (
		<div className="flex min-w-0 flex-wrap items-center gap-1.5">
			{playbookBadge}
			{humanForwardedBadge}
			{testNumberBadge}
		</div>
	);
	const tags = callTags.length > 0 && (
		<div className="flex min-w-0 max-w-full flex-wrap items-center gap-1.5 lg:flex-nowrap">
			{callTags.map((tag) => (
				<Badge
					key={tag.id}
					variant="secondary"
					title={tag.label}
					className={`min-w-0 max-w-full shrink-0 lg:shrink text-xs px-2 py-0 h-5 ${getServiceTagClassName(tag.color, { deleted: Boolean(tag.deletedAt) })}`}
				>
					<span className="truncate">{tag.label}</span>
				</Badge>
			))}
		</div>
	);

	return (
		<div
			data-testid={`call-row-${call.id}`}
			data-call-id={call.id}
			onClick={call.isBlocked ? undefined : onClick}
			onKeyDown={(event) => {
				if (
					call.isBlocked ||
					event.target !== event.currentTarget ||
					(event.key !== "Enter" && event.key !== " ")
				) {
					return;
				}
				event.preventDefault();
				onClick();
			}}
			role={call.isBlocked ? undefined : "link"}
			tabIndex={call.isBlocked ? undefined : 0}
			aria-label={
				call.isBlocked
					? undefined
					: i18n._({
							id: "openCallFrom",
							message: "Open call from {caller}",
							values: {
								caller: participants.caller.name || participants.caller.phone,
							},
						})
			}
			className={`min-w-0 max-w-full rounded-lg border-[1.5px] shadow-card p-3 transition-[border-color,box-shadow,background-color] ${
				call.isBlocked
					? "cursor-not-allowed border-destructive/20 bg-danger-tint opacity-60"
					: call.isTestCall
						? "cursor-pointer border-ring/30 bg-accent/40 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30"
						: `hover:border-ring border-card-border cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${participants.isHuman && !participants.isOutbound ? "bg-warn-tint" : isPlaybookCall ? "bg-live-tint" : isNew ? "bg-muted" : "bg-card"}`
			}`}
		>
			{/* Row 1: Primary scan row */}
			<div
				className={`grid grid-cols-1 items-start gap-3 ${compact ? "" : "lg:grid-cols-[240px_minmax(0,1fr)_fit-content(220px)]"}`}
			>
				{/* Left: Participants (Caller on desktop / Target participant on mobile) */}
				<div className="min-w-0 space-y-1.5">
					<div
						className={`flex w-full flex-shrink-0 items-center gap-2.5 ${compact ? "" : "lg:w-[240px]"}`}
					>
						{/* Primary Participant Column */}
						<div className="flex flex-col gap-0.5 min-w-0 flex-1">
							{/* Mobile: Caller (inbound) or Callee (outbound) */}
							<span
								className={`truncate text-sm ${compact ? "" : "lg:hidden"} ${isNew ? "font-bold text-foreground" : "text-foreground"}`}
								dir="auto"
							>
								{mobileParticipant.name}
							</span>
							{/* Desktop: Caller */}
							<span
								className={`hidden truncate text-center text-sm ${compact ? "" : "lg:block"} ${isNew ? "font-bold text-foreground" : "text-foreground"}`}
								dir="auto"
							>
								{participants.caller.name}
							</span>

							{/* Mobile phone */}
							<span
								className={`line-clamp-2 break-all text-xs ${compact ? "" : "lg:hidden"} ${isNew ? "font-bold text-muted-foreground" : "text-muted-foreground"}`}
								dir="ltr"
							>
								{formatPhoneNumber(mobileParticipant.phone)}
							</span>
							{/* Desktop phone */}
							<span
								className={`hidden truncate text-center text-xs ${compact ? "" : "lg:block"} ${isNew ? "font-bold text-muted-foreground" : "text-muted-foreground"}`}
								dir="ltr"
							>
								{formatPhoneNumber(participants.caller.phone)}
							</span>
						</div>

						{/* Arrow (Desktop only) */}
						{!compact &&
							(language === "he" ? (
								<ArrowLeft className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 self-center hidden lg:block" />
							) : (
								<ArrowRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 self-center hidden lg:block" />
							))}

						{/* Callee (Desktop only) */}
						<div
							className={`min-w-0 flex-1 flex-col gap-0.5 ${compact ? "hidden" : "hidden lg:flex"}`}
						>
							<span
								className={`text-sm text-center truncate ${isNew ? "font-bold text-foreground" : "text-foreground"}`}
							>
								{participants.callee.name === "Human Operator"
									? t({ id: "humanOperator", message: "Human Operator" })
									: participants.callee.name}
							</span>
							<span
								className={`text-xs text-center truncate ${isNew ? "font-bold text-muted-foreground" : "text-muted-foreground"}`}
								dir="ltr"
							>
								{formatPhoneNumber(participants.callee.phone) || "—"}
							</span>
						</div>

						{/* Blocked Badge */}
						{call.isBlocked && (
							<Badge
								variant="destructive"
								className="text-[10px] px-1 py-0 h-4 shrink-0"
							>
								{t({ id: "blocked", message: "Blocked" })}
							</Badge>
						)}

						{/* Mobile: Date/Time */}
						<div
							className={`${compact ? "flex" : "flex lg:hidden"} shrink-0 flex-col items-end whitespace-nowrap text-xs ${isNew ? "font-bold text-muted-foreground" : "text-muted-foreground"}`}
						>
							<span>
								{t({ id: "caseId_label", message: "Case ID" })}:{" "}
								{formatCaseId(call.caseId)}
							</span>
							<span>
								{call.date} · {call.time}
							</span>
						</div>
					</div>
				</div>

				{/* Middle: Call Title + Summary (Desktop) */}
				<div
					className={`min-w-0 flex-1 flex-col gap-1 ${compact ? "hidden" : "hidden lg:flex"}`}
				>
					{(call.title || shouldShowStatusFallback) && (
						<h3
							className={`text-sm truncate flex items-center gap-1.5 ${isNew ? "font-bold text-foreground" : "text-foreground"} ${shouldShowStatusFallback ? "text-muted-foreground italic" : ""}`}
							dir="auto"
						>
							{isProcessingCall && (
								<Loader2 className="w-3.5 h-3.5 animate-spin" />
							)}
							{isFailedWithoutTranscript && (
								<AlertCircle className="w-3.5 h-3.5" />
							)}
							{shouldShowNoAudio && <MicOff className="w-3.5 h-3.5" />}
							<span className="min-w-0 truncate">
								{shouldShowStatusFallback ? fallbackTitle : call.title}
							</span>
						</h3>
					)}
					{(call.summary || shouldShowStatusFallback) && (
						<p
							className={`text-xs w-full truncate ${isNew ? "font-medium text-muted-foreground" : "text-muted-foreground"} ${shouldShowStatusFallback ? "text-muted-foreground italic" : ""}`}
							dir="auto"
						>
							{shouldShowStatusFallback ? fallbackSummary : call.summary}
						</p>
					)}

					{actionItems.length > 0 && (
						<div className="flex min-w-0 items-center gap-3 text-xs text-muted-foreground">
							{actionItems.map((item, index) => (
								<div
									key={index}
									className="flex flex-1 items-center gap-1.5 min-w-0"
								>
									<FileText
										className={`w-3 h-3 flex-shrink-0 ${item.status === "closed" ? "text-muted-foreground" : "text-foreground"}`}
									/>
									<span
										className={`truncate ${isNew ? "font-medium text-foreground" : "text-muted-foreground"}`}
										dir="auto"
									>
										{item.action.type === "followUp"
											? item.action.details
											: `Booking: ${item.action.accommodationType}`}
									</span>
								</div>
							))}
						</div>
					)}
					<div className="mt-1 flex min-w-0 flex-col gap-1.5">
						{tags}
						{specialBadges}
					</div>
				</div>

				{/* Extras: action icons above end-aligned case/date/time. */}
				<div
					className={`${compact ? "hidden" : "hidden lg:flex"} min-w-0 self-stretch flex-col justify-between gap-2`}
				>
					<div className="flex items-center justify-end gap-2">
						<div className="flex shrink-0 items-center gap-1">{actions}</div>
					</div>
					<div
						className="truncate text-end text-[11px] text-muted-foreground"
						title={`#${formatCaseId(call.caseId)} · ${call.date} · ${call.time}`}
					>
						<bdi dir="ltr">
							#{formatCaseId(call.caseId)} · {call.date} · {call.time}
						</bdi>
					</div>
				</div>
			</div>

			{/* Mobile: Title & Summary */}
			<div
				className={`${compact ? "flex" : "flex lg:hidden"} my-1.5 flex-col gap-1`}
			>
				{(call.title || shouldShowStatusFallback) && (
					<h3
						className={`text-xs sm:text-sm truncate flex items-center gap-1.5 ${isNew ? "font-bold text-foreground" : "text-foreground"} ${shouldShowStatusFallback ? "text-muted-foreground italic" : ""}`}
						dir="auto"
					>
						{isProcessingCall && <Loader2 className="w-3 h-3 animate-spin" />}
						{isFailedWithoutTranscript && <AlertCircle className="w-3 h-3" />}
						{shouldShowNoAudio && <MicOff className="w-3 h-3" />}
						<span className="min-w-0 truncate">
							{shouldShowStatusFallback ? fallbackTitle : call.title}
						</span>
					</h3>
				)}
				{(call.summary || shouldShowStatusFallback) && (
					<p
						className={`text-xs line-clamp-2 ${isNew ? "font-medium text-muted-foreground" : "text-muted-foreground"} ${shouldShowStatusFallback ? "text-muted-foreground italic" : ""}`}
						dir="auto"
					>
						{shouldShowStatusFallback ? fallbackSummary : call.summary}
					</p>
				)}
			</div>

			{/* Mobile keeps the stacked layout and its existing participant display. */}
			<div
				className={`${compact ? "flex" : "flex lg:hidden"} mt-2 items-center justify-between gap-2 border-t border-border pt-1`}
			>
				<div className="min-w-0 flex-1 space-y-1.5">
					{tags}
					{specialBadges}
				</div>
				{!call.isBlocked && (
					<div className="flex shrink-0 items-center gap-1.5">{actions}</div>
				)}
			</div>
		</div>
	);
}
